import { Capacity } from "../App";

interface CapacityObject {
    name: string;
    description: string;
    keywords: string;
    linkedCapacitiesName: string[];
    lifeCourseDevelopmentTitle: string;
    lifeCourseDevelopmentValue: string;
    orderedCapacitiesWidth: number[];
    lottieJSONFileName: string;
    learnMoreLink: string;
}

export enum CapacityNames {
    General = "Reflecting",
    Reflecting = "Reflecting",
    Listening = "Listening",
    DiscerningPatterns = "Discerning Patterns",
    Inquiring = "Inquiring",
    Embodying = "Embodying",
    Relaxing = "Relaxing",
    Empathizing = "Empathizing",
    Sensing = "Sensing",
    Observing = "Observing"
}

const GENERAL_OBJ: CapacityObject = {
    name: "What are Core Capacities?",
    description: "Evidence suggests that developing specific core capacities from childhood can support performance in school, work, and life.<br/><br/>These nine “core capacities” are cornerstones of life skills. We often overlook these capacities as innate basic skills, so they are underutilized in efforts to promote child well-being and development.<br/><br/>But by nurturing, expanding, and modelling these capacities, children can better understand and interact with the world around them, and realise their unique potential.",
    keywords: "",
    linkedCapacitiesName: [],
    lifeCourseDevelopmentTitle: "",
    lifeCourseDevelopmentValue: "",
    orderedCapacitiesWidth: [100, 100, 100, 100, 100, 100, 100, 100, 100],
    lottieJSONFileName: "",
    learnMoreLink: ""
};

const REFLECTING_OBJ: CapacityObject = {
    name: CapacityNames.Reflecting,
    description: "The capacity to explore other views: pondering, looking back, projecting forward, and offering feedback.",
    keywords: "Self-reflection, and metacognition",
    linkedCapacitiesName: [CapacityNames.DiscerningPatterns, CapacityNames.Empathizing, CapacityNames.Relaxing],
    lifeCourseDevelopmentTitle: "Onset",
    lifeCourseDevelopmentValue: "2 years; increases with age.",
    orderedCapacitiesWidth: [0, 80, 100, 80, 80, 100, 100, 80, 80],
    lottieJSONFileName: "Anim-Capacities-Reflecting",
    learnMoreLink: "https://www.unicef-irc.org/publications/pdf/What-Makes-Me_Core-Capacities-for-Living-and-Learning.pdf#page=45"
};

const LISTENING_OBJ: CapacityObject = {
    name: CapacityNames.Listening,
    description: "The capacity to tune in to what surrounds, connecting to words, sounds, spaces in oneself, others, and the environment.",
    keywords: "Active listening",
    linkedCapacitiesName: [CapacityNames.Reflecting, CapacityNames.Empathizing, CapacityNames.Relaxing, CapacityNames.Inquiring],
    lifeCourseDevelopmentTitle: "Onset",
    lifeCourseDevelopmentValue: "from utero, spatial listening from 1.5 years; spatial listening improves with age 1-8 years.",
    orderedCapacitiesWidth: [100, 0, 80, 100, 80, 100, 100, 80, 80],
    lottieJSONFileName: "Anim-Capacities-Listening",
    learnMoreLink: "https://www.unicef-irc.org/publications/pdf/What-Makes-Me_Core-Capacities-for-Living-and-Learning.pdf#page=39"
};

const DISCERNING_PATTERNS_OBJ: CapacityObject = {
    name: CapacityNames.DiscerningPatterns,
    description: "The capacity to perceive what is interconnected, recognizing interdependency and the relationship of the parts to the whole.",
    keywords: "Discerning patterns, discernment, systems thinking, strategic self-regulation, conformity, working memory, understanding patterns, pattern recognition, complex thinking, and complex information",
    linkedCapacitiesName: [CapacityNames.Reflecting, CapacityNames.Empathizing, CapacityNames.Sensing, CapacityNames.Observing],
    lifeCourseDevelopmentTitle: "Onset",
    lifeCourseDevelopmentValue: "4-5 years, strategic thinking and self-regulation increase with age.",
    orderedCapacitiesWidth: [100, 80, 0, 80, 80, 80, 100, 100, 100],
    lottieJSONFileName: "Anim-Capacities-Discerning Patterns",
    learnMoreLink: "https://www.unicef-irc.org/publications/pdf/What-Makes-Me_Core-Capacities-for-Living-and-Learning.pdf#page=25"
};

const INQUIRING_OBJ: CapacityObject = {
    name: CapacityNames.Inquiring,
    description: "The capacity to seek what can expand knowledge and actions, tracking, asking, and uncovering an experience with openness and curiosity.",
    keywords: "Inquiry, and asking questions",
    linkedCapacitiesName: [CapacityNames.Reflecting, CapacityNames.Observing, CapacityNames.Listening, CapacityNames.DiscerningPatterns, CapacityNames.Sensing, CapacityNames.Embodying],
    lifeCourseDevelopmentTitle: "Onset",
    lifeCourseDevelopmentValue: "0-1 year (preverbal); Function inquiry 2-4 years; meaningful inquiry, from 2 years onwards.",
    orderedCapacitiesWidth: [100, 100, 100, 0, 100, 80, 80, 100, 100],
    lottieJSONFileName: "Anim-Capacities-Inquiring",
    learnMoreLink: "https://www.unicef-irc.org/publications/pdf/What-Makes-Me_Core-Capacities-for-Living-and-Learning.pdf#page=35"
};

const EMBODYING_OBJ: CapacityObject = {
    name: CapacityNames.Embodying,
    description: "The capacity to relate to the body as a source of knowing, nurturing, stimulating, and expanding our multiple senses.",
    keywords: "Sensory awareness, sensory stimulation, sensory expansion, sensory integration and sensory enrichment",
    linkedCapacitiesName: [CapacityNames.Reflecting, CapacityNames.Empathizing, CapacityNames.Inquiring, CapacityNames.Listening, CapacityNames.DiscerningPatterns, CapacityNames.Relaxing, CapacityNames.Observing],
    lifeCourseDevelopmentTitle: "Onset of sensory processing",
    lifeCourseDevelopmentValue: "2-3 years; music capacity from age 8.",
    orderedCapacitiesWidth: [100, 100, 100, 100, 0, 100, 100, 80, 100],
    lottieJSONFileName: "Anim-Capacities-Embodying",
    learnMoreLink: "https://www.unicef-irc.org/publications/pdf/What-Makes-Me_Core-Capacities-for-Living-and-Learning.pdf#page=28"
};

const RELAXING_OBJ: CapacityObject = {
    name: CapacityNames.Relaxing,
    description: "The capacity to ease tensions in thoughts, feelings, and body, opening to physical, emotional, mental ease.",
    keywords: "Relaxation, mindfulness, meditation, no/lack of stress, being calm, anti-stressed, tense, and unwind",
    linkedCapacitiesName: [CapacityNames.Reflecting, CapacityNames.Empathizing, CapacityNames.Sensing],
    lifeCourseDevelopmentTitle: "Onset",
    lifeCourseDevelopmentValue: "Early childhood; mindfulness develops in preschool years into adulthood.",
    orderedCapacitiesWidth: [100, 80, 80, 80, 80, 0, 100, 100, 80],
    lottieJSONFileName: "Anim-Capacities-Relaxing",
    learnMoreLink: "https://www.unicef-irc.org/publications/pdf/What-Makes-Me_Core-Capacities-for-Living-and-Learning.pdf#page=48"
};

const EMPATHIZING_OBJ: CapacityObject = {
    name: CapacityNames.Empathizing,
    description: "The capacity to know, see, and feel with and through others, active and deliberate resonance with their thoughts, feelings, and experiences.",
    keywords: "Empathy, compassion, sympathy, and connection",
    linkedCapacitiesName: [CapacityNames.Reflecting, CapacityNames.Observing, CapacityNames.DiscerningPatterns],
    lifeCourseDevelopmentTitle: "Onset",
    lifeCourseDevelopmentValue: "0-3 years; cognitive empathy increases with age 0-17; affective empathy falls with age.",
    orderedCapacitiesWidth: [100, 80, 100, 80, 80, 80, 0, 80, 100],
    lottieJSONFileName: "Anim-Capacities-Empathizing",
    learnMoreLink: "https://www.unicef-irc.org/publications/pdf/What-Makes-Me_Core-Capacities-for-Living-and-Learning.pdf#page=31"
};

const SENSING_OBJ: CapacityObject = {
    name: CapacityNames.Sensing,
    description: "The capacity to engage with what is not visible, acknowledging and expanding non-rational ways of knowing.",
    keywords: "Subtle sensing, imagination, intuition, and resonance",
    linkedCapacitiesName: [],
    lifeCourseDevelopmentTitle: "Onset",
    lifeCourseDevelopmentValue: "From pre-school; divergent thinking from 2 years.",
    orderedCapacitiesWidth: [80, 80, 80, 80, 80, 80, 80, 0, 80],
    lottieJSONFileName: "Anim-Capacities-Sensing",
    learnMoreLink: "https://www.unicef-irc.org/publications/pdf/What-Makes-Me_Core-Capacities-for-Living-and-Learning.pdf#page=51"
};

const OBSERVING_OBJ: CapacityObject = {
    name: CapacityNames.Observing,
    description: "The capacity to place attention, widely and closely; to be keen, engaged, and consistently noticing.",
    keywords: "Noticing, observation, concentration, focusing attention, paying attention, detecting, and becoming aware of",
    linkedCapacitiesName: [CapacityNames.DiscerningPatterns, CapacityNames.Inquiring, CapacityNames.Reflecting, CapacityNames.Sensing],
    lifeCourseDevelopmentTitle: "Onset",
    lifeCourseDevelopmentValue: "pre-school age; non-linear development based on cues.",
    orderedCapacitiesWidth: [100, 80, 100, 100, 80, 80, 80, 100, 0],
    lottieJSONFileName: "Anim-Capacities-Observing",
    learnMoreLink: "https://www.unicef-irc.org/publications/pdf/What-Makes-Me_Core-Capacities-for-Living-and-Learning.pdf#page=42"
};

export class CapacityContent {
    name: string;
    description: string;
    keywords: string;
    linkedCapacitiesName: string[];
    lifeCourseDevelopmentTitle: string;
    lifeCourseDevelopmentValue: string;
    orderedCapacitiesWidth: number[];
    lottieJSONFileName: string;
    learnMoreLink: string;

    constructor(obj: CapacityObject) {
        this.name = obj.name;
        this.description = obj.description;
        this.keywords = obj.keywords;
        this.linkedCapacitiesName = obj.linkedCapacitiesName;
        this.lifeCourseDevelopmentTitle = obj.lifeCourseDevelopmentTitle;
        this.lifeCourseDevelopmentValue = obj.lifeCourseDevelopmentValue;
        this.orderedCapacitiesWidth = obj.orderedCapacitiesWidth;
        this.lottieJSONFileName = obj.lottieJSONFileName;
        this.learnMoreLink = obj.learnMoreLink;
    }
}

export class CapacitiesHelper {
    static readonly generalCapacity = new CapacityContent(GENERAL_OBJ)
    static readonly reflectingCapacity = new CapacityContent(REFLECTING_OBJ)
    static readonly listeningCapacity = new CapacityContent(LISTENING_OBJ)
    static readonly discerningPatternsCapacity = new CapacityContent(DISCERNING_PATTERNS_OBJ)
    static readonly inquiringCapacity = new CapacityContent(INQUIRING_OBJ)
    static readonly embodyingCapacity = new CapacityContent(EMBODYING_OBJ)
    static readonly relaxingCapacity = new CapacityContent(RELAXING_OBJ)
    static readonly empathizingCapacity = new CapacityContent(EMPATHIZING_OBJ)
    static readonly sensingCapacity = new CapacityContent(SENSING_OBJ)
    static readonly observingCapacity = new CapacityContent(OBSERVING_OBJ)

    static capacityContentForCapacity(capacity: Capacity) {
        switch (capacity) {
            case Capacity.Reflecting:
                return this.reflectingCapacity;
            case Capacity.Listening:
                return this.listeningCapacity;
            case Capacity.DiscerningPatterns:
                return this.discerningPatternsCapacity;
            case Capacity.Inquiring:
                return this.inquiringCapacity;
            case Capacity.Embodying:
                return this.embodyingCapacity;
            case Capacity.Relaxing:
                return this.relaxingCapacity;
            case Capacity.Empathizing:
                return this.empathizingCapacity;
            case Capacity.Sensing:
                return this.sensingCapacity;
            case Capacity.Observing:
                return this.observingCapacity;
            default:
                return this.generalCapacity;
        }
    }

    static capacityNameForCapacity(capacity: Capacity) {
        return this.capacityContentForCapacity(capacity).name;
    }
}